import { Component, OnInit, OnDestroy, HostListener, Input, ChangeDetectorRef } from '@angular/core';
import { MsalService, MSAL_GUARD_CONFIG, MsalGuardConfiguration } from '@azure/msal-angular';
import { HttpClient, HttpClientModule, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { MsalBroadcastService } from '@azure/msal-angular';
import { EventMessage, EventType } from '@azure/msal-browser';
import { InteractionStatus, InteractionType } from '@azure/msal-browser';
import { filter } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { NgxSpinnerService } from "ngx-spinner";
import { AppComponent } from '../app/app.component';
import { AppInsightsService } from '../../service/app-insights.service';
import { citationBlocksForModal } from './citation-blocks';


interface BenefitsHandoff {
  show: true;
  url: string;
  intro: string;
  link_text: string;
  detail: string;
}

interface Message {
  text: string;
  isUser: boolean;
  isAbbreviatedTitle?: boolean;
  title?: string;
  content?: string;
  citationObject?: any;
  sourceType?: 'cba' | 'handbook';
  blocks?: any[];
  id?: string;
  quotedTexts?: string[];
  benefitsHandoff?: BenefitsHandoff;
}

@Component({
  selector: 'app-home',
  templateUrl: './home.component.html',
  styleUrl: './home.component.scss'
})
export class HomeComponent implements OnInit, OnDestroy {
  readonly benefitsAiUrl = 'https://benefitsai.swapa.org';
  query: string = '';
  isSubmitDisabled: boolean = false;
  queryId: string = '';
  response: string = '';
  questionDisplay: string = '';
  isFormVisible: boolean = true;
  showFeedback: boolean = false;
  showComment: boolean = false;
  comment: string = '';
  origin = environment.apiOrigin
  loginDisplay = false
  messages: Message[] = []; // Updated type
  specialBotResponse: string = ``;
  showSpecialButtons: boolean = false;
  mostRecentBotMessageIndex: number | null = null;
  matchingObjects: any[] = [];
  quotedTexts: string[] = [];
  oldContractObjects: any[] = [];
  filteredMatchingObjects: any[] = []; // for implementation dates
  showModal: boolean = false; // Modal visibility
  modalData: any = {}; // Data for the modal display
  private modalImageUrls = new Set<string>();
  private modalImageLoadVersion = 0;
  showScore: boolean = false;
  score: number | null = null;
  scoreComment: string = '';
  scoreError: string | null = null;
  userFirstName: string = '';
  userName: string = '';
  welcomeMessage: string = '';
  allowedUsers: string[] = ['Matthew Monahan', 'Kandi Doming', 'Brenda Burkham', 'Jeanna Christopher', 'Ryne Ray', 'Jana Fuller', 'Belstephen JeDai', 'Maria Almanza', 'Lexi Taylor', 'Cathy Cox']
  showSplash: boolean = false; // Add this line

  // Part of Global fix
  private readonly CONVO_KEY = 'contractai_conversation_id';
  private readonly PENDING_QUERY_KEY = 'contractai_pending_query';
  private readonly AUTH_INTERRUPTED_KEY = 'contractai_auth_interrupted';
  private loadingStatusTimers: ReturnType<typeof setTimeout>[] = [];
  loadingStatusText: string = 'Analyzing your question…';
  conversationId: string = '';

  // Schduling Handbook toggle
  private readonly SOURCE_KEY = 'contractai_source';
  currentSource: 'cba' | 'handbook' | 'both' = (sessionStorage.getItem(this.SOURCE_KEY) as any) || 'both';


  private generateConversationId(): string {
    // Browser-safe UUID. Works on modern browsers.
    if (crypto && 'randomUUID' in crypto) {
      return crypto.randomUUID();
    }
    // Fallback (older browsers)
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = (crypto.getRandomValues(new Uint8Array(1))[0] & 15) >> 0;
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  private ensureConversationId(): string {
    let id = sessionStorage.getItem(this.CONVO_KEY);
    if (!id) {
      id = this.generateConversationId();
      sessionStorage.setItem(this.CONVO_KEY, id);
    }
    this.conversationId = id;
    return id;
  }

  private resetConversationId(): string {
    const id = this.generateConversationId();
    sessionStorage.setItem(this.CONVO_KEY, id);
    this.conversationId = id;
    return id;
  }

  private isAuthFailure(error: any): boolean {
    const backendCode = error?.error?.error;
    const errorText = [
      error?.message,
      error?.errorMessage,
      error?.errorCode,
      String(error || '')
    ].join(' ').toLowerCase();

    return (error instanceof HttpErrorResponse && error.status === 401 && (backendCode === 'AUTH_EXPIRED' || backendCode === 'AUTH_REQUIRED'))
      || errorText.includes('auth_interaction_required')
      || errorText.includes('interaction_required')
      || errorText.includes('login_required');
  }

  private restoreQuestionAfterAuthFailure(query: string, removeLoadingMessage = true): void {
    if (removeLoadingMessage) {
      this.stopLoadingExperience();
    }

    this.query = query;
    sessionStorage.setItem(this.PENDING_QUERY_KEY, query);
    this.isSubmitDisabled = false;
    this.showFeedback = false;
    this.messages.push({
      text: 'Your session expired. Please sign in again, then submit your question when you return.',
      isUser: false
    });
    this.changeDetectorRef.detectChanges();
  }

  private hasAcronymCandidate(query: string): boolean {
    return /\b[A-Z][A-Z0-9-]{1,9}\b/.test(query);
  }

  private sourceLoadingStatus(source: 'cba' | 'handbook' | 'both'): string {
    const statusBySource = {
      cba: 'Finding relevant sections of the CBA…',
      handbook: 'Finding relevant sections of the Handbook…',
      both: 'Finding relevant sections of the CBA and Handbook…'
    };

    return statusBySource[source];
  }

  private startLoadingExperience(
    query: string,
    source: 'cba' | 'handbook' | 'both',
    statuses?: string[]
  ): void {
    this.stopLoadingExperience();

    const loadingStatuses = statuses || [
      'Analyzing your question…',
      ...(this.hasAcronymCandidate(query) ? ['Retrieving relevant acronyms…'] : []),
      this.sourceLoadingStatus(source),
      'Preparing your answer…'
    ];

    this.loadingStatusText = loadingStatuses[0];
    this.messages.push({ text: 'loading', isUser: false });

    loadingStatuses.slice(1).forEach((status, index) => {
      const timer = setTimeout(() => {
        this.loadingStatusText = status;
        this.changeDetectorRef.detectChanges();
      }, (index + 1) * 1800);
      this.loadingStatusTimers.push(timer);
    });
  }

  private stopLoadingExperience(): void {
    this.loadingStatusTimers.forEach(timer => clearTimeout(timer));
    this.loadingStatusTimers = [];

    const loadingMessageIndex = this.messages.findIndex(message => message.text === 'loading');
    if (loadingMessageIndex !== -1) {
      this.messages.splice(loadingMessageIndex, 1);
    }
  }
  // End of Global fix


  constructor(private http: HttpClient, 
    private changeDetectorRef: ChangeDetectorRef,
    private msalBroadcastService: MsalBroadcastService,
    private msalService: MsalService,
    private appInsights: AppInsightsService, 
    private spinner: NgxSpinnerService,
    private appComponent: AppComponent) { }

    ngOnInit(): void {
      // this.ensureConversationId();
      this.resetConversationId();

      
      this.appInsights.logInfo('ngOnInit called', {
        source: 'home.component.ts',
        function: 'ngOnInit'
      });
    
      this.msalBroadcastService.msalSubject$
        .pipe(
          filter((msg: EventMessage) => msg.eventType === EventType.LOGIN_SUCCESS),
        )
        .subscribe((result: EventMessage) => {
          console.log('LOGIN_SUCCESS event received');
          this.appInsights.logInfo('MSAL LOGIN_SUCCESS received', {
            source: 'home.component.ts',
            function: 'ngOnInit',
            msalEvent: result
          });
        });

      // If source problems later check this logic
      const storedSource = sessionStorage.getItem(this.SOURCE_KEY) as 'cba' | 'handbook' | 'both' | null;

      if (!storedSource) {
        sessionStorage.setItem(this.SOURCE_KEY, 'both');
        this.currentSource = 'both';
      } else {
        this.currentSource = storedSource;
      }

      // Scheduling Handbook toggle listener
      window.addEventListener('contractai-source-changed', (e: any) => {
        this.stopLoadingExperience();
        const newSource = (e?.detail?.source as 'cba' | 'handbook' | 'both') || 'both';
        this.currentSource = newSource;

        // Reset conversation + UI state when flipping sources
        this.resetConversationId();
        this.messages = [];
        this.matchingObjects = [];
        this.filteredMatchingObjects = [];
        this.oldContractObjects = [];
        this.quotedTexts = [];
        this.response = '';
        this.queryId = '';
        this.showFeedback = false;
        this.showComment = false;
        this.comment = '';
        this.showModal = false;
        this.modalData = {};
        this.isFormVisible = true;
        this.specialBotResponse = '';
        this.showSpecialButtons = false;
        this.mostRecentBotMessageIndex = null;
        this.isSubmitDisabled = false;
        this.query = '';
        sessionStorage.removeItem(this.PENDING_QUERY_KEY);
        sessionStorage.removeItem(this.AUTH_INTERRUPTED_KEY);
      });
    
      // const conversation_id = this.ensureConversationId();

      // this.http.get(`${this.origin}/clear?conversation_id=${encodeURIComponent(conversation_id)}`, { responseType: 'text' })
      //   .subscribe({
      //     next: () => {
      //       this.appInsights.logInfo('Conversation cleared on init', {
      //         source: 'home.component.ts',
      //         function: 'ngOnInit',
      //         conversation_id
      //       });
      //     },
      //     error: (err) => {
      //       this.appInsights.logException(err, 'home.component.ts', 'ngOnInit');
      //     }
      //   });

    
      this.fetchUserInfo();

      const pendingQuery = sessionStorage.getItem(this.PENDING_QUERY_KEY);
      if (pendingQuery) {
        this.query = pendingQuery;
        if (sessionStorage.getItem(this.AUTH_INTERRUPTED_KEY) === 'true') {
          this.messages.push({
            text: 'Your session was refreshed. Your question is still here, so you can submit it again.',
            isUser: false
          });
          sessionStorage.removeItem(this.AUTH_INTERRUPTED_KEY);
        }
      }

      // Only show splash if just logged in
      if (sessionStorage.getItem('justLoggedIn') === 'true') {
        this.showSplash = true;
        sessionStorage.removeItem('justLoggedIn');
      }
    }

    ngOnDestroy(): void {
      this.stopLoadingExperience();
      this.modalImageLoadVersion++;
      this.revokeModalImageUrls();
    }
    
    // Part of Global fix - deleting this block    
    // @HostListener('window:beforeunload', ['$event'])
    // beforeUnloadHandler(event: any) {
    //   this.http.get('/clear').subscribe({
    //     next: () => {
    //       this.appInsights.logInfo('Conversation cleared on window unload', {
    //         source: 'home.component.ts',
    //         function: 'beforeUnloadHandler'
    //       });
    //     },
    //     error: (err) => {
    //       this.appInsights.logException(err, 'home.component.ts', 'beforeUnloadHandler');
    //     }
    //   });
    // }
    
    
    onSubmit(event?: Event) {
      if (event) event.preventDefault();
    
      this.isSubmitDisabled = true; // Disable the submit button
      this.changeDetectorRef.detectChanges(); // Trigger change detection
      this.specialBotResponse = '';
      const query = this.query;
      this.query = '';
      this.showFeedback = false;
      this.messages.push({ text: query, isUser: true });
    
      const conversation_id = this.ensureConversationId();
      // Scheduling Handbook toggle
      const source = (sessionStorage.getItem(this.SOURCE_KEY) as any);
      //const source = 'both';
      if (!source) {
        // You said you'd rather fail hard than silently default
        this.messages.push({ text: 'Source is not set. Please refresh and try again.', isUser: false });
        this.isSubmitDisabled = false;
        return;
      }

      this.startLoadingExperience(query, source);

      const body = new URLSearchParams({ query, conversation_id, source });
      sessionStorage.setItem(this.PENDING_QUERY_KEY, query);

    
      this.http.post<any>(`${this.origin}/question`, `${body}`, { responseType: 'json' })
        .subscribe({
          next: (data) => {
            sessionStorage.removeItem(this.PENDING_QUERY_KEY);
            this.stopLoadingExperience();
    
            this.response = data.response;
            this.matchingObjects = data.matching_objects;
            this.queryId = data.query_id || '';
            this.quotedTexts = data.quoted_text || [];
    
            // this.filteredMatchingObjects = this.matchingObjects.filter(
            //   obj => obj.implementation_date && obj.implementation_date.length > 0
            // );
            this.filteredMatchingObjects = [];
    
            let implementationDates: string[] = [];
            if (this.filteredMatchingObjects.length > 0) {
              implementationDates = this.filteredMatchingObjects.flatMap(obj => obj.implementation_date);
              const formattedDates = implementationDates.join('\n');
              this.response += `\n\nReview context for Implementation notes:\n\n${formattedDates}`;
              this.response = this.colorizeDates(this.response, implementationDates);
            }
    
            const benefitsHandoff = (
              data.benefits_handoff?.show === true
              && data.benefits_handoff?.url === this.benefitsAiUrl
            ) ? data.benefits_handoff as BenefitsHandoff : undefined;
            this.messages.push({
              text: this.response,
              isUser: false,
              benefitsHandoff
            });
            this.mostRecentBotMessageIndex = this.messages.length - 1;
    
            this.displayAbbreviatedTitles();
            this.showFeedback = true;
            //this.hideForm();
    
            this.oldContractObjects = data.old_contract_objects || [];
            if (this.oldContractObjects.length > 0) {
              this.handleOldContractObjects(query, this.oldContractObjects);
            }
    
            this.isSubmitDisabled = false; // Enable the submit button
            this.changeDetectorRef.detectChanges(); // Trigger change detection
            this.appInsights.logInfo('onSubmit successful', {
              source: 'home.component.ts',
              function: 'onSubmit',
              conversation_id,
              queryPreview: query.slice(0, 100),
              responsePreview: this.response.slice(0, 100),
              benefitsHandoffShown: !!benefitsHandoff
            });
          },
          error: (err) => {
            this.stopLoadingExperience();
            this.isSubmitDisabled = false; // Enable the submit button
            this.changeDetectorRef.detectChanges(); // Trigger change detection
            if (err?.status === 422 && err?.error?.error === 'CONTENT_FILTERED') {
              sessionStorage.removeItem(this.PENDING_QUERY_KEY);
              this.query = query; // Keep the question available for rephrasing.
              this.queryId = '';
              this.showFeedback = false;
              this.showComment = false;
              this.showScore = false;
              this.messages.push({
                text: err.error.message || 'An automated content filter blocked this request. Please rephrase your question using specific contract or scheduling terms and try again.',
                isUser: false
              });
              this.appInsights.logWarning('Question blocked by automated content filter', {
                source: 'home.component.ts',
                function: 'onSubmit',
                outcome: 'content_filtered'
              });
              this.changeDetectorRef.detectChanges();
              return;
            }

            this.appInsights.logException(err, 'home.component.ts', 'onSubmit');
            if (this.isAuthFailure(err)) {
              this.restoreQuestionAfterAuthFailure(query, false);
              return;
            }

            sessionStorage.removeItem(this.PENDING_QUERY_KEY);
            // Use backend error message if available, else fallback to generic
            let errorMsg = 'An error occurred. Please refresh the browser and try again.';
            if (err?.error?.message && err?.error?.error !== 'AUTH_EXPIRED' && err?.error?.error !== 'AUTH_REQUIRED') {
              errorMsg = err.error.message;
            } else if (typeof err?.error === 'string' && err.error !== 'Invalid or expired token') {
              errorMsg = err.error;
            }
            this.messages.push({ text: errorMsg, isUser: false });
          }
        });
    
      const textareaElement = document.querySelector('textarea[name="query"]') as HTMLTextAreaElement;
      if (textareaElement) {
        textareaElement.style.height = 'auto';
      }
    }    

  onSpecialSubmit(): void {
    this.appInsights.logInfo('onSpecialSubmit triggered', {
      source: 'home.component.ts',
      function: 'onSpecialSubmit',
      user: this.userName
    });
  
    this.specialBotResponse = '';
    const query = this.query;
    this.showFeedback = false;
    this.startLoadingExperience(query, this.currentSource, [
      'Analyzing your question…',
      'Rewriting your question…'
    ]);
    const body = new URLSearchParams({ query: this.query });
    this.query = '';
  
    this.http.post<any>(`${this.origin}/question-assist`, `${body}`, { responseType: 'json' })
      .subscribe({
        next: (data) => {
          this.stopLoadingExperience();
          if (data.warning) {
            alert(data.warning);
          }
          this.specialBotResponse = data.response[0];
          this.showSpecialButtons = true;
          this.showFeedback = false;
  
          this.appInsights.logInfo('onSpecialSubmit successful', {
            source: 'home.component.ts',
            function: 'onSpecialSubmit',
            queryPreview: query.slice(0, 100),
            responsePreview: data.response[0].slice(0, 100)
          });
        },
        error: (error) => {
          this.stopLoadingExperience();
          if (this.isAuthFailure(error)) {
            this.query = query;
            this.showSpecialButtons = false;
            this.specialBotResponse = '';
            alert('Your session expired. Please sign in again, then try rewriting your question when you return.');
            this.changeDetectorRef.detectChanges();
            return;
          }

          alert("An error occurred while rewriting your question. Please try again.");
          this.appInsights.logException(error, 'home.component.ts', 'onSpecialSubmit');
        }
      });
  }
  
  
  
  onSubmitC2012(query: string, oldContractObjects: any[], event?: Event) {
    if (event) event.preventDefault();
  
    this.appInsights.logInfo('onSubmitC2012 triggered', {
      source: 'home.component.ts',
      function: 'onSubmitC2012',
      queryPreview: query.slice(0, 100)
    });
  
    this.specialBotResponse = '';
    this.showFeedback = false;
  
    const context = JSON.stringify(oldContractObjects);
    const body = new URLSearchParams({ query: query, context: context });
    this.messages = [];
  
    this.messages.push({ text: query, isUser: true });
    this.startLoadingExperience(query, 'cba', [
      'Analyzing your question…',
      'Finding relevant sections of C2012…',
      'Preparing your answer…'
    ]);
  
    this.http.post<any>(`${this.origin}/questionC2012`, `${body}`, { responseType: 'json' })
      .subscribe({
        next: (data) => {
          this.stopLoadingExperience();
          this.spinner.hide();
          this.response = `With reference to C2012: ${data.response}`;
          this.messages.push({ text: this.response, isUser: false });
          this.mostRecentBotMessageIndex = this.messages.length - 1;
  
          this.queryId = data.query_id || '';
          this.matchingObjects = data.matching_objects;
          this.quotedTexts = data.quoted_text || [];
          this.displayAbbreviatedTitles();
          this.query = '';
          this.showFeedback = true;
  
          this.appInsights.logInfo('onSubmitC2012 successful', {
            source: 'home.component.ts',
            function: 'onSubmitC2012',
            responsePreview: this.response.slice(0, 100),
            queryId: this.queryId
          });
        },
        error: (error) => {
          this.spinner.hide();
          this.stopLoadingExperience();
          if (this.isAuthFailure(error)) {
            this.restoreQuestionAfterAuthFailure(query, false);
            this.appInsights.logException(error, 'home.component.ts','onSubmitC2012');
            return;
          }

          this.messages.push({ text: 'An error occurred. Please try again.', isUser: false });
  
          this.appInsights.logException(error, 'home.component.ts','onSubmitC2012');
        }
      });
  }
  
  

  private isMobileDevice(): boolean {
    const navigatorWithHints = navigator as Navigator & {
      userAgentData?: { mobile?: boolean };
    };

    if (navigatorWithHints.userAgentData?.mobile === true) {
      return true;
    }

    const isIPadOS = navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
    return isIPadOS || /Android|webOS|iPhone|iPad|iPod|IEMobile|Opera Mini/i.test(navigator.userAgent);
  }

  // Desktop Enter submits; mobile/tablet Enter remains available for new lines.
  onKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.isComposing && !this.isMobileDevice()) {
      event.preventDefault();  // Prevents the new line

      // Check if the input is empty or the submit button is disabled
      if (!this.query || this.query.trim() === '' || this.isSubmitDisabled) {
        console.log('Submission prevented: Query is empty or button is disabled.');
        return; // Do not trigger form submission
      }

      this.onSubmit();  // Trigger form submission instead
    }
  }  

  handleOldContractObjects(query: string, oldContractObjects: any[]): void {
    this.appInsights.logInfo('handleOldContractObjects called', {
      source: 'home.component.ts',
      function: 'handleOldContractObjects',
      queryPreview: query.slice(0, 100),
      count: oldContractObjects?.length || 0
    });
  
    const titles = Array.isArray(this.oldContractObjects)
      ? this.oldContractObjects.map(obj => obj.abbreviated_title).join(', ')
      : 'No references available';
  
    const message = `For this question it is recommended that you reference section ${titles} of C2012. As the related sections in the current CBA have yet to be implemented. Press Ok to proceed or press Cancel to decline.`;
  
    try {
      const userConfirmed = confirm(message);
      if (userConfirmed) {
        this.appInsights.logInfo('User confirmed proceeding with C2012 context', {
          source: 'home.component.ts',
          function: 'handleOldContractObjects',
        });
        this.onSubmitC2012(query, oldContractObjects);
      } else {
        this.appInsights.logInfo('User declined to proceed with C2012 context', {
          source: 'home.component.ts',
          function: 'handleOldContractObjects',
        });
      }
    } catch (err) {
      this.appInsights.logException(err, 'home.component.ts', 'handleOldContractObjects');
    }
  }
  


  handleSubmitSpecialResponse(): void {
    this.appInsights.logInfo('handleSubmitSpecialResponse called', {
      source: 'home.component.ts',
      function: 'handleSubmitSpecialResponse',
      queryPreview: this.specialBotResponse.slice(0, 100)
    });
    this.query = this.specialBotResponse;
    this.onSubmit();
    this.clearSpecialResponse();
  }
  
  handleEditSpecialResponse(): void {
    this.appInsights.logInfo('handleEditSpecialResponse called', {
      source: 'home.component.ts',
      function: 'handleEditSpecialResponse',
      responsePreview: this.specialBotResponse.slice(0, 100)
    });
    this.query = this.specialBotResponse;
    this.clearSpecialResponse();
  }
  

  clearSpecialResponse(): void {
    this.appInsights.logInfo('clearSpecialResponse called', {
      source: 'home.component.ts',
      function: 'clearSpecialResponse'
    });
    this.specialBotResponse = '';
    this.showSpecialButtons = false;
  }  

  // change color of implementation dates
  colorizeDates(text: string, dates: string[]): string {
    if (!dates || dates.length === 0) return text;
    dates.forEach(date => {
      text = text.replace(date, `<span class="red-text">${date}</span>`);
    });
    return text;
  }
  
  openModal(msgOrObj: any): void {
    const matchingObject = msgOrObj?.citationObject || msgOrObj;
    const sourceType = (matchingObject?.source_type || matchingObject?.sourceType || this.currentSource).toLowerCase();
    const blocks = citationBlocksForModal(matchingObject?.blocks || [], sourceType);
    const quotedTexts = Array.isArray(msgOrObj?.quotedTexts)
      ? msgOrObj.quotedTexts.filter((quote: unknown): quote is string => typeof quote === 'string')
      : [];

    this.modalImageLoadVersion++;
    this.revokeModalImageUrls();
    const imageLoadVersion = this.modalImageLoadVersion;

    this.appInsights.logInfo('openModal called', {
      source: 'home.component.ts',
      function: 'openModal',
      sourceType,
      label: matchingObject?.abbreviated_title || matchingObject?.title || 'N/A'
    });

    this.modalData = {
      ...matchingObject,
      sourceType,
      title: matchingObject?.title || '',
      abbreviated_title: matchingObject?.abbreviated_title || '',
      blocks,
      content: matchingObject?.content || '',
      quotedTexts
    };

    this.showModal = true;
    if (sourceType === 'handbook') {
      this.loadHandbookImages(blocks, imageLoadVersion);
    }
  }

  private loadHandbookImages(blocks: any[], imageLoadVersion: number): void {
    blocks
      .filter((block: any) => block?.type === 'image')
      .forEach((block: any) => {
        if (!block.blob_path) {
          block.image_error = true;
          return;
        }

        block.image_loading = true;
        block.image_error = false;
        this.http.get(`${this.origin}/handbook-image`, {
          params: { blob_path: block.blob_path },
          responseType: 'blob'
        }).subscribe({
          next: (imageBlob: Blob) => {
            const imageUrl = URL.createObjectURL(imageBlob);
            if (imageLoadVersion !== this.modalImageLoadVersion || !this.showModal) {
              URL.revokeObjectURL(imageUrl);
              return;
            }

            block.image_url = imageUrl;
            block.image_loading = false;
            this.modalImageUrls.add(imageUrl);
          },
          error: (error) => {
            if (imageLoadVersion !== this.modalImageLoadVersion) return;
            block.image_loading = false;
            block.image_error = true;
            this.appInsights.logException(error, 'home.component.ts', 'loadHandbookImages');
          }
        });
      });
  }

  private revokeModalImageUrls(): void {
    this.modalImageUrls.forEach((imageUrl) => URL.revokeObjectURL(imageUrl));
    this.modalImageUrls.clear();
  }

  
  closeModal(): void {
    this.appInsights.logInfo('closeModal called', {
      source: 'home.component.ts',
      function: 'closeModal'
    });
  
    this.showModal = false;
    this.modalImageLoadVersion++;
    this.revokeModalImageUrls();
    this.modalData = {};
  }
  
  // displayAbbreviatedTitles(): void {
  //   // const sourceType: 'cba' | 'handbook' =
  //   //   (this.matchingObjects?.[0]?.source_type as any) || this.currentSource;
  //   const sourceType = ((this.matchingObjects?.[0]?.source_type as string) || this.currentSource).toLowerCase();


  //   this.appInsights.logInfo('displayAbbreviatedTitles called', {
  //     source: 'home.component.ts',
  //     function: 'displayAbbreviatedTitles',
  //     sourceType,
  //     count: this.matchingObjects?.length || 0
  //   });

  //   this.matchingObjects.forEach(obj => {
  //     if (sourceType === 'cba') {
  //       if (obj.abbreviated_title) {
  //         this.messages.push({
  //           text: obj.abbreviated_title,
  //           isUser: false,
  //           isAbbreviatedTitle: true,
  //           sourceType: 'cba',
  //           // title: obj.title,
  //           // content: obj.content
  //           citationObject: obj
  //         });
  //       }
  //       return;
  //     }

  //     // Handbook: clickable label is title
  //     if (obj.title) {
  //       this.messages.push({
  //         text: obj.title,
  //         isUser: false,
  //         isAbbreviatedTitle: true,     // keep existing template behavior for now
  //         sourceType: 'handbook',
  //         // id: obj.id,
  //         // title: obj.title,
  //         // blocks: obj.blocks || []
  //         citationObject: obj
  //       });
  //     }
  //   });

  //   this.mostRecentBotMessageIndex = this.messages.length - 1;
  // }

  displayAbbreviatedTitles(): void {
    this.appInsights.logInfo('displayAbbreviatedTitles called', {
      source: 'home.component.ts',
      function: 'displayAbbreviatedTitles',
      currentSource: this.currentSource,
      count: this.matchingObjects?.length || 0
    });

    this.matchingObjects.forEach(obj => {
      const objSourceType = ((obj.source_type || obj.sourceType || this.currentSource) as string).toLowerCase();
      const quotedTexts = Array.isArray(obj.evidence_quotes)
        ? obj.evidence_quotes.filter((quote: unknown): quote is string => typeof quote === 'string')
        : [];

      if (objSourceType === 'cba' && obj.abbreviated_title) {
        this.messages.push({
          text: obj.abbreviated_title,
          isUser: false,
          isAbbreviatedTitle: true,
          sourceType: 'cba',
          citationObject: obj,
          quotedTexts
        });
        return;
      }

      if (objSourceType === 'handbook' && obj.title) {
        this.messages.push({
          text: obj.title,
          isUser: false,
          isAbbreviatedTitle: true,
          sourceType: 'handbook',
          citationObject: obj,
          quotedTexts
        });
      }
    });

    this.mostRecentBotMessageIndex = this.messages.length - 1;
  }

  // Method to hide form
  hideForm() {
    this.isFormVisible = false;
  }

  showForm() {
    this.isFormVisible = true;
  }

  // Maybe revisit this later. Refreshing the page just calls all the right clear functions so its easier for now.
  // Method to reload the application
// Method to reload the application
resetApplication() {
  this.appInsights.logInfo('Application reset triggered', {
    source: 'home.component.ts',
    function: 'resetApplication'
  });

  this.showSplash = false;
  this.resetConversationId();
  sessionStorage.removeItem(this.PENDING_QUERY_KEY);
  sessionStorage.removeItem(this.AUTH_INTERRUPTED_KEY);
  window.location.reload();
  
}

// Send Feedback by searching for the query id in DB
sendFeedback(isPositive: boolean | null = null, score: number | null = null) {
  this.showFeedback = false;

  if (score !== null && (score < 0 || score > 10)) {
    alert('Score must be between 0 and 10.');
    return;
  }

  if (!this.queryId) {
    const message = 'Query ID is missing. Cannot send feedback.';
    this.appInsights.logException(new Error(message), 'home.component.ts', 'sendFeedback');
    alert("Error: Query ID is missing. Please ask a question first before submitting feedback.");
    return;
  }

  const feedbackType = isPositive === null ? 'thinking' : (isPositive ? '1' : '0');
  const body = new URLSearchParams({
    query_id: this.queryId,
    feedback: feedbackType,
    comment: this.comment,
    ...(score !== null && { score: score.toString() })
  });

  this.spinner.show();
  this.http.post(`${this.origin}/feedback`, body.toString(), { responseType: 'text' }).subscribe({
    next: () => {
      this.spinner.hide();
      alert('Feedback received! Thank you.');
      this.showFeedback = false;
      this.showComment = false;
      this.comment = '';
      this.showScore = false;
      this.score = null;

      this.appInsights.logInfo('Feedback submitted successfully', {
        source: 'home.component.ts',
        function: 'sendFeedback',
        queryId: this.queryId,
        score: score ?? undefined
      });
    },
    error: (error) => {
      this.spinner.hide();
      if (this.isAuthFailure(error)) {
        alert('Your session expired. Please sign in again, then submit your feedback when you return.');
        this.appInsights.logException(error, 'home.component.ts', 'sendFeedback');
        return;
      }

      alert('Error sending feedback. Please try again.');
      this.appInsights.logException(error, 'home.component.ts', 'sendFeedback');
    }
  });
}

// Show comment section
showCommentSection() {
  this.appInsights.logInfo('Comment section shown', {
    source: 'home.component.ts',
    function: 'showCommentSection'
  });
  this.showComment = true;
  this.showFeedback = false;
}

// Submit comment
submitComment() {
  this.appInsights.logInfo('Comment submitted', {
    source: 'home.component.ts',
    function: 'submitComment'
  });
  this.sendFeedback(false);
}

  autoResize(textareaEvent: Event) {
    const textarea = textareaEvent.target as HTMLTextAreaElement;
    if (textarea) {
        textarea.style.height = 'auto';
        textarea.style.height = textarea.scrollHeight + 'px';
    }
  }

  ///////////////////////////////
  // Method to show the score form
  showScoreForm() {
    this.showScore = true;
    this.scoreError = null; // Clear any previous errors
    this.showFeedback = false;
  }

  // Validate the score input
  validateScore() {
    if (this.score === null || this.score < 0 || this.score > 10) {
        this.scoreError = 'Score must be a number between 1 and 10.';
    } else {
        this.scoreError = null; // Clear the error if the input is valid
    }
  }

  // Method to submit the score and comment
  submitScore() {
    this.validateScore();
  
    if (!this.scoreError) {
      const feedbackData = {
        score: this.score,
        comment: this.scoreComment,
      };
  
      this.appInsights.logInfo('Score feedback submitted', {
        source: 'home.component.ts',
        function: 'submitScore',
        score: this.score,
        hasComment: !!this.scoreComment
      });
  
      // You could optionally send feedbackData to backend here
  
      this.showScore = false;
      this.score = null;
      this.scoreComment = '';
    }
  }  

  //Fetch User Info
  fetchUserInfo() {
    this.http.get(`${this.origin}/user-info`).subscribe({
      next: (info: any) => {
        this.userFirstName = info.user_name.split(' ')[0];
        this.userName = info.user_name;
        this.setWelcomeMessage();
  
        this.appInsights.logInfo('User info fetched successfully', {
          source: 'home.component.ts',
          function: 'fetchUserInfo',
          userName: this.userName
        });
      },
      error: (err) => {
        this.appInsights.logException(err, 'home.component.ts', 'fetchUserInfo');
  
        if (this.isAuthFailure(err)) {
          alert('Your session expired. Please sign in again.');
          return;
        }
  
        alert('Error fetching user information. Please log out and log back in. Click "OK" to log out.');
        this.appComponent.logout();
      }
    });
  }
  

  // New Method: Add Welcome Message
  setWelcomeMessage() {
    this.welcomeMessage = `Hello ${this.userFirstName}!`;
  }

  isUserAllowed(): boolean {
    return this.allowedUsers.includes(this.userName);
  }

  closeSplash(): void {
    this.showSplash = false;
  }

  splashPrimaryAction(): void {
    window.open('https://www.swapa.org/globalassets/internal/documents/contract_admin/ContractAI_BetaGuide.pdf', '_blank', 'noopener');
  }

  splashAcknowledge(): void {
    // Do something on secondary button click
    this.closeSplash();
  }

  emptySubtitleBySource: Record<string, string> = {
    cba: 'You’re viewing the CBA. Ask a contract related question to get started.',
    handbook: 'You’re viewing the Scheduling Handbook. Ask a Scheduling Handbook related question to get started.',
    both: 'You’re viewing both the CBA and the Scheduling Handbook. Ask a question to get started.'
  };

  get emptySubtitleText(): string {
    return this.emptySubtitleBySource[this.currentSource] ?? 'Ask a question to get started.';
  }
}



